import { AuthService } from './auth.service';
import { ApiService } from '@ag2-weathermaker/shared/services/data/api.service';
import { DataService } from '@ag2-weathermaker/shared/services/data/data.service';
import { MapsService } from '@ag2-weathermaker/shared/services/maps.service';
import { DataRegion } from '@ag2-weathermaker/shared/types/enums';
import { UserAllowedResponse } from '@ag2-weathermaker/shared/types/main-content.types';
import { inject, Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  private authService = inject(AuthService);
  private apiService = inject(ApiService);
  private router = inject(Router);
  private dataService = inject(DataService);
  private mapsService = inject(MapsService);

  canActivate(): Observable<boolean> {
    if (this.authService.hasValidAccessToken()) {
      return this.validateUserAccess();
    }

    // Access token isn't valid, attempt to refresh it.
    return this.tryRefreshingToken().pipe(
      switchMap((tokenRefreshed) => (tokenRefreshed ? this.validateUserAccess() : of(this.redirectToLogin()))),
      catchError(() => of(this.redirectToLogin()))
    );
  }

  private validateUserAccess(): Observable<boolean> {
    return this.apiService.isUserAllowed().pipe(
      map((userAllowed: UserAllowedResponse) => {
        if (!userAllowed?.is_allowed) {
          return this.redirectToUnauthorized();
        }

        const defaultRegion = userAllowed.default_region ?? DataRegion.NA;
        this.dataService.setDefaultRegion(defaultRegion);

        const superSecretMode = userAllowed.super_secret_mode ?? false;
        this.dataService.setSuperSecretMode(superSecretMode);

        const isInternalUser = userAllowed.permissions.includes('WM__Access_Internal') ?? false;

        this.dataService.setIsInternalUser(isInternalUser);

        this.mapsService.reinitializeMapConfigForRegion(defaultRegion);
        return true;
      }),
      catchError(() => of(this.redirectToUnauthorized()))
    );
  }

  private tryRefreshingToken(): Observable<boolean> {
    if (this.authService.getRefreshToken()) {
      return this.authService.refreshToken().pipe(
        map(() => true),
        catchError(() => of(false))
      );
    } else {
      return of(false);
    }
  }

  private redirectToLogin(): false {
    this.router.navigate(['/login']);
    return false;
  }

  private redirectToUnauthorized(): false {
    this.router.navigate(['/unauthorized']);
    return false;
  }
}
